import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Gem, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, Fetcher, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input, Field } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/providers/UiProviders'
import {
  fetchCountriesPremium,
  upsertPremiumPricing,
  deletePremiumPricing,
  GEO_STR as S,
  type CountryPremiumRow,
  type PremiumPricing,
} from './geoApi'

/**
 * A11.23 — Davlat bo'yicha Premium narxlari. REAL `/admin/countries` ro'yxati +
 * per-davlat greenfield overlay (`/admin/geo/premium-restrictions`): har davlat
 * uchun tuzilgan tahrir (yoqilgan/valyuta/oy-yil narxi/sinov-kun/funksiyalar).
 * Saqlash → upsert `POST {code, value}`; o'chirish → `DELETE :code`. Backend hali
 * chiqmagan bo'lsa overlay bo'sh (fail-soft) — ro'yxat baribir davlatlar bilan
 * to'ladi, tahrir upsert'ni birinchi yozuv sifatida yaratadi.
 */

const EMPTY: PremiumPricing = {
  enabled: false,
  currency: '',
  price_month: 0,
  price_year: 0,
  trial_days: 0,
  features: [],
}

/** Tahrir-formasi holati — barcha maydon matn (raqamlar saqlashda parse). */
interface Draft {
  enabled: boolean
  currency: string
  price_month: string
  price_year: string
  trial_days: string
  features: string
}

function toDraft(p: PremiumPricing): Draft {
  return {
    enabled: p.enabled,
    currency: p.currency,
    price_month: String(p.price_month),
    price_year: String(p.price_year),
    trial_days: String(p.trial_days),
    features: p.features.join(', '),
  }
}

function numOf(s: string): number {
  const n = Number(s.trim())
  return Number.isFinite(n) && n >= 0 ? n : 0
}

export function PremiumPricingScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [edit, setEdit] = useState<{ row: CountryPremiumRow; draft: Draft } | null>(null)
  const [busy, setBusy] = useState(false)

  const reload = () => setReloadKey((k) => k + 1)

  const columns = useMemo<AdminColumn<CountryPremiumRow>[]>(
    () => [
      {
        key: 'flag',
        header: '',
        type: 'text',
        width: 44,
        sortable: false,
        filterable: false,
        render: (r) => <span className="text-base">{r.flag || '🌐'}</span>,
      },
      { key: 'code', header: S.code, type: 'id', width: 76 },
      {
        key: 'name',
        header: S.country,
        type: 'text',
        minWidth: 170,
        render: (r) => (
          <Link to={`/geo/${r.code}`} className="text-accent hover:underline">
            {r.name}
          </Link>
        ),
      },
      { key: 'users', header: S.registrations, type: 'number', width: 104 },
      {
        key: 'is_blocked',
        header: S.status,
        type: 'enum',
        width: 130,
        accessor: (r) => (r.is_blocked ? 'blocked' : 'open'),
        options: [
          { value: 'open', label: S.open, status: 'active' },
          { value: 'blocked', label: S.blocked, status: 'banned' },
        ],
      },
      {
        key: 'enabled',
        header: S.premiumEnabled,
        type: 'enum',
        width: 120,
        sortable: false,
        // pv yo'q → '' → enum yacheykasi avtomatik «—» ko'rsatadi (cells.tsx).
        accessor: (r) => (r.pv ? (r.pv.enabled ? 'on' : 'off') : ''),
        options: [
          { value: 'on', label: S.premiumOn, status: 'active' },
          { value: 'off', label: S.premiumOff, status: 'expired' },
        ],
      },
      {
        key: 'currency',
        header: S.premiumCurrency,
        type: 'text',
        width: 96,
        sortable: false,
        accessor: (r) => r.pv?.currency ?? '',
        render: (r) =>
          r.pv?.currency ? (
            <span className="font-mono tabular text-primary">{r.pv.currency}</span>
          ) : (
            <span className="text-tertiary">{S.none}</span>
          ),
      },
      {
        key: 'price_month',
        header: S.premiumMonth,
        type: 'number',
        width: 108,
        align: 'right',
        sortable: false,
        accessor: (r) => r.pv?.price_month ?? null,
        render: (r) =>
          r.pv ? (
            <span className="font-mono tabular text-primary">
              {r.pv.price_month.toLocaleString()}
            </span>
          ) : (
            <span className="text-tertiary">{S.none}</span>
          ),
      },
      {
        key: 'price_year',
        header: S.premiumYear,
        type: 'number',
        width: 108,
        align: 'right',
        sortable: false,
        accessor: (r) => r.pv?.price_year ?? null,
        render: (r) =>
          r.pv ? (
            <span className="font-mono tabular text-primary">
              {r.pv.price_year.toLocaleString()}
            </span>
          ) : (
            <span className="text-tertiary">{S.none}</span>
          ),
      },
      {
        key: 'trial_days',
        header: S.premiumTrial,
        type: 'number',
        width: 116,
        align: 'right',
        sortable: false,
        accessor: (r) => r.pv?.trial_days ?? null,
        render: (r) =>
          r.pv ? (
            <span className="font-mono tabular text-secondary">{r.pv.trial_days}</span>
          ) : (
            <span className="text-tertiary">{S.none}</span>
          ),
      },
      {
        key: 'features',
        header: S.premiumFeatures,
        type: 'text',
        minWidth: 160,
        sortable: false,
        accessor: (r) => (r.pv?.features ?? []).join(', '),
        render: (r) => {
          const f = r.pv?.features ?? []
          return f.length ? (
            <span className="text-secondary">{f.join(', ')}</span>
          ) : (
            <span className="text-tertiary">{S.none}</span>
          )
        },
      },
    ],
    [],
  )

  const fetcher = useCallback<Fetcher<CountryPremiumRow>>(fetchCountriesPremium, [])

  const rowActions = useMemo<RowAction<CountryPremiumRow>[]>(
    () => [
      {
        id: 'edit',
        label: S.premiumEditAction,
        icon: <Pencil size={13} />,
        permission: PERMISSIONS.COUNTRIES_BLOCK,
        onSelect: (r) => setEdit({ row: r, draft: toDraft(r.pv ?? EMPTY) }),
      },
      {
        id: 'delete',
        label: S.premiumRemove,
        icon: <Trash2 size={13} />,
        danger: true,
        permission: PERMISSIONS.COUNTRIES_BLOCK,
        visible: (r) => Boolean(r.pv),
        onSelect: async (r) => {
          try {
            await deletePremiumPricing(r.code)
            toast(S.premiumRemoved, { tone: 'success' })
            reload()
          } catch {
            toast(S.opFailed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const patch = (next: Partial<Draft>) =>
    setEdit((e) => (e ? { ...e, draft: { ...e.draft, ...next } } : e))

  const save = async () => {
    if (!edit) return
    setBusy(true)
    try {
      const d = edit.draft
      const value: PremiumPricing = {
        enabled: d.enabled,
        currency: d.currency.trim(),
        price_month: numOf(d.price_month),
        price_year: numOf(d.price_year),
        trial_days: numOf(d.trial_days),
        features: d.features
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      }
      await upsertPremiumPricing(edit.row.code, value)
      toast(S.premiumSaved, { tone: 'success' })
      setEdit(null)
      reload()
    } catch {
      toast(S.opFailed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title={S.premiumTitle}
        screenId="A11.23"
        description={S.premiumNote}
        actions={
          <Link
            to="/geo"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.backToList}
          </Link>
        }
      />
      <DataTable<CountryPremiumRow>
        key={reloadKey}
        screenId="A11.23"
        permission={PERMISSIONS.COUNTRIES_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.code}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Gem size={28} strokeWidth={1.5} /> }}
      />

      {edit && (
        <Dialog
          open
          onClose={() => setEdit(null)}
          title={`${S.premiumEditTitle} · ${edit.row.name}`}
          description={edit.row.code}
          icon={<Gem size={18} strokeWidth={1.6} />}
          footer={
            <>
              <Button variant="primary" disabled={busy} onClick={save}>
                {busy ? S.premiumSaving : S.premiumSave}
              </Button>
              <div className="flex-1" />
              <Button variant="default" onClick={() => setEdit(null)}>
                {S.premiumCancel}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-secondary">
              <Checkbox
                checked={edit.draft.enabled}
                onChange={(e) => patch({ enabled: e.target.checked })}
              />
              {S.premiumEnabledLabel}
            </label>

            <div className="flex flex-wrap gap-2">
              <Field label={S.premiumCurrency} className="w-28">
                <Input
                  mono
                  value={edit.draft.currency}
                  onChange={(e) => patch({ currency: e.target.value })}
                  placeholder={S.premiumCurrencyPh}
                  spellCheck={false}
                  maxLength={8}
                />
              </Field>
              <Field label={S.premiumMonth} className="w-32">
                <Input
                  mono
                  inputMode="numeric"
                  value={edit.draft.price_month}
                  onChange={(e) => patch({ price_month: e.target.value })}
                  placeholder="0"
                />
              </Field>
              <Field label={S.premiumYear} className="w-32">
                <Input
                  mono
                  inputMode="numeric"
                  value={edit.draft.price_year}
                  onChange={(e) => patch({ price_year: e.target.value })}
                  placeholder="0"
                />
              </Field>
              <Field label={S.premiumTrialDays} className="w-40">
                <Input
                  mono
                  inputMode="numeric"
                  value={edit.draft.trial_days}
                  onChange={(e) => patch({ trial_days: e.target.value })}
                  placeholder="0"
                />
              </Field>
            </div>

            <Field label={S.premiumFeatures} hint={S.premiumFeaturesHint}>
              <Input
                value={edit.draft.features}
                onChange={(e) => patch({ features: e.target.value })}
                placeholder={S.premiumFeaturesPh}
                spellCheck={false}
              />
            </Field>
          </div>
        </Dialog>
      )}
    </>
  )
}
